/*
 * Утилиты вложений мессенджера (фото/файлы/голос). Полностью изолированы от остального
 * приложения. Лимиты дублируют серверные (ТЗ 12.4): типы и размер проверяются и
 * на клиенте (для UX), и на бэке (для безопасности).
 */
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
export const MAX_ATTACHMENTS = 10;

export const ALLOWED_TYPES = [
    'image/jpeg',
    'image/png',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'audio/webm',
    'audio/mp4',
    'audio/aac',
    'audio/ogg',
    'audio/x-m4a',
    'audio/mpeg',
];

export const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'pdf', 'doc', 'docx', 'xls', 'xlsx', 'webm', 'm4a', 'ogg', 'mp4', 'aac'];

export const ACCEPT_ATTR = '.jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx,image/jpeg,image/png,application/pdf';

const getExtension = (name) => (name && name.indexOf('.') !== -1 ? name.split('.').pop().toLowerCase() : '');

export const normalizeContentType = (contentType) => (contentType ? String(contentType).split(';')[0].trim().toLowerCase() : '');

export const isImageAttachment = (attachment) =>
    !!attachment &&
    ((attachment.contentType && attachment.contentType.indexOf('image/') === 0) ||
        ['jpg', 'jpeg', 'png'].includes(getExtension(attachment.name)));

export const isAudioAttachment = (attachment) => {
    if (!attachment) {
        return false;
    }

    const baseType = normalizeContentType(attachment.contentType);

    if (baseType && baseType.indexOf('audio/') === 0) {
        return true;
    }

    return ['webm', 'm4a', 'ogg', 'mp4', 'aac'].includes(getExtension(attachment.name));
};

const isAllowedType = (contentType, name) => {
    const baseType = normalizeContentType(contentType);

    if (baseType && ALLOWED_TYPES.includes(baseType)) {
        return true;
    }

    return ALLOWED_EXTENSIONS.includes(getExtension(name));
};

/**
 * Проверка одного файла. Возвращает ключ ошибки ('attachmentUnsupported' |
 * 'attachmentTooLarge') или null, если файл валиден.
 */
export const validateFile = (file) => {
    if (!file) {
        return 'attachmentUnsupported';
    }

    if (!isAllowedType(file.type, file.name)) {
        return 'attachmentUnsupported';
    }

    if (file.size > MAX_ATTACHMENT_BYTES) {
        return 'attachmentTooLarge';
    }

    return null;
};

export const validateVoiceBlob = (blob, contentType) => {
    if (!blob || blob.size === 0) {
        return 'voiceRecordFailed';
    }

    const name = voiceFileName(contentType);

    if (!isAllowedType(contentType, name)) {
        return 'attachmentUnsupported';
    }

    if (blob.size > MAX_ATTACHMENT_BYTES) {
        return 'attachmentTooLarge';
    }

    return null;
};

export const extensionForAudioType = (contentType) => {
    const baseType = normalizeContentType(contentType);

    if (baseType.includes('mp4') || baseType.includes('aac') || baseType.includes('m4a')) {
        return 'm4a';
    }

    if (baseType.includes('ogg')) {
        return 'ogg';
    }

    return 'webm';
};

export const voiceFileName = (contentType) => {
    const stamp = new Date().toISOString().replace(/[:.]/g, '-');

    return `voice-${stamp}.${extensionForAudioType(contentType)}`;
};

/**
 * Читает File в нормализованное вложение { name, contentType, size, data, preview }.
 * data — base64 без префикса (для бэка), preview — data-URL для предпросмотра картинок/аудио.
 */
export const readFileAsAttachment = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () => {
            const result = String(reader.result || '');
            const base64 = result.split(',')[1] || '';
            const isImage = file.type && file.type.indexOf('image/') === 0;
            const isAudio = isAudioAttachment({ contentType: file.type, name: file.name });

            resolve({
                name: file.name,
                contentType: normalizeContentType(file.type) || file.type,
                size: file.size,
                data: base64,
                preview: isImage || isAudio ? result : undefined,
            });
        };

        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
    });

export const readBlobAsAttachment = (blob, name, contentType) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        const normalizedType = normalizeContentType(contentType) || 'audio/webm';

        reader.onload = () => {
            const result = String(reader.result || '');

            resolve({
                name,
                contentType: normalizedType,
                size: blob.size,
                data: result.split(',')[1] || '',
                preview: result,
            });
        };

        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(blob);
    });

export const audioBlobUrlFromBase64 = (data, contentType) => {
    if (!data || typeof document === 'undefined' || typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') {
        return null;
    }

    const binary = atob(String(data));
    const bytes = new Uint8Array(binary.length);

    for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
    }

    const blob = new Blob([bytes], { type: normalizeContentType(contentType) || 'audio/webm' });

    return URL.createObjectURL(blob);
};

export const revokeAudioBlobUrl = (url) => {
    if (url && typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') {
        URL.revokeObjectURL(url);
    }
};

/**
 * Сохраняет/открывает вложение через data-URL (веб и Cordova-webview).
 * Безопасно в среде без DOM (тесты) — просто no-op.
 */
export const triggerAttachmentDownload = (attachment) => {
    if (typeof document === 'undefined' || !attachment) {
        return;
    }

    const href =
        attachment.preview ||
        (attachment.data ? `data:${attachment.contentType || 'application/octet-stream'};base64,${attachment.data}` : null);

    if (!href) {
        return;
    }

    const link = document.createElement('a');
    link.href = href;
    link.download = attachment.name || 'attachment';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};
